import { type Opportunity, type User } from "@entities";
import { Chip, Stack, Typography } from "@mui/material";
import type { AvailabilityBase } from "@shared/entities/availability-base";
import { type ColumnProps } from "@shared/frontend/hyper-table";
import { type CoreRow } from "@tanstack/react-table";
import PhoneNumberCell from "../PhoneNumberCell";
import { WEEK_DAYS_HEB } from "@shared/helpers/constants";
import {
  formatOpportunityEndDisplay,
  formatOpportunitySchedule,
  formatOpportunityStartDisplay,
} from "@shared/helpers/format-opportunity-schedule";
import { buildCommunicationRecordsColumn } from "../communication-records-column";
import { OpportunityHandlingControls } from "../../../components/opportunity-handling/OpportunityHandlingControls";
import { partnerOrganizationTableColumn } from "../../../components/partner-organization-select/OpportunityPartnerOrganizationCell";

export function formatDaysSchedule(availability: AvailabilityBase[]): string {
  const daysMap = ["א׳", "ב׳", "ג׳", "ד׳", "ה׳", "ו׳", "ש׳"];

  const timesMap = {
    morning: "בוקר",
    afternoon: "צהריים",
    evening: "ערב",
  };

  const times = {
    morning: [] as number[],
    afternoon: [] as number[],
    evening: [] as number[],
  };

  [...availability]
    .sort((a, b) => a.dow - b.dow)
    .forEach((a) => {
      if (a.MORNING) {
        times.morning.push(a.dow);
      }
      if (a.AFTERNOON) {
        times.afternoon.push(a.dow);
      }
      if (a.EVENING) {
        times.evening.push(a.dow);
      }
    });

  return Object.entries(times)
    .filter(([_k, v]) => v.length > 0)
    .map(
      ([k, v]) =>
        `${timesMap[k as keyof typeof times]} - ${v.map((d) => daysMap[d]).join(",")}`,
    )
    .join(" ");
}

function aidRequestInstances(opportunity: Opportunity) {
  return (opportunity.childOpportunities ?? [])
    .filter((child) => !child.isDeleted)
    .sort((a, b) => {
      const aTime = a.startDate ? new Date(a.startDate).getTime() : 0;
      const bTime = b.startDate ? new Date(b.startDate).getTime() : 0;
      return aTime - bTime;
    });
}

function instanceLine(child: Opportunity) {
  const place = (child.location ?? "").trim();
  const when = formatOpportunitySchedule(child);
  return [place, when].filter(Boolean).join(" - ");
}

export const useAssociationRequestedColumns = (
  onViewCommunicationRecords: (opportunity: Opportunity) => void,
): ColumnProps<Opportunity>[] => {
  return [
      {
        field: "title",
        enableSorting: true,
        cell: ({ row }: { row: CoreRow<Opportunity> }) => {
          const instances = aidRequestInstances(row.original);
          return (
            <Stack gap={0.25} sx={{ minWidth: 0, py: 0.5 }}>
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography sx={{ fontSize: "inherit" }}>{row.original.title}</Typography>
                {instances.length >= 2 && (
                  <Chip
                    size="small"
                    label={`${instances.length} מופעים`}
                    sx={{ height: 22, fontWeight: 650 }}
                  />
                )}
              </Stack>
              {instances.length >= 2
                ? instances.map((child, index) => (
                    <Typography
                      key={child.id}
                      variant="caption"
                      color="text.secondary"
                      sx={{ lineHeight: 1.35 }}
                    >
                      {`מופע ${index + 1}: ${instanceLine(child)}`}
                    </Typography>
                  ))
                : null}
            </Stack>
          );
        },
      },
      {
        field: "handlingManager.name",
        enableSorting: false,
        preventRowClick: true,
        minSize: 130,
        caption: "בטיפול",
        cell: ({ row }) => (
          <OpportunityHandlingControls
            opportunityId={row.original.id}
            handlingManagerId={row.original.handlingManagerId}
            handlingManagerName={row.original.handlingManager?.name}
            handlingSince={row.original.handlingSince}
            variant="compact"
          />
        ),
      },
      partnerOrganizationTableColumn,
      {
        field: "categories",
        caption: "קטגוריה",
        cell: ({ row }: { row: CoreRow<Opportunity> }) => {
          const categories = row.original.categories;
          if (!categories?.length) return " - ";
          const names = categories
            .map((oc) => (oc as { category?: { name: string } })?.category?.name)
            .filter(Boolean);
          return names.length ? names.join(", ") : " - ";
        },
        minSize: 120,
      },
      {
        field: "startDate",
        cell: ({ row }: { row: CoreRow<Opportunity>; }) => {
          const instances = aidRequestInstances(row.original);
          if (instances.length >= 2 && !row.original.startDate) {
            return <span>לפי מופע</span>;
          }
          return (
            <span title={formatOpportunityStartDisplay(row.original)}>
              {formatOpportunityStartDisplay(row.original)}
            </span>
          );
        },
      },
      {
        field: "endDate",
        cell: ({ row }: { row: CoreRow<Opportunity>; }) => {
          const instances = aidRequestInstances(row.original);
          if (instances.length >= 2 && !row.original.endDate) {
            return <span>לפי מופע</span>;
          }
          return (
            <span title={formatOpportunityEndDisplay(row.original)}>
              {formatOpportunityEndDisplay(row.original)}
            </span>
          );
        },
      },
      {
        field: "location",
        cell: ({ row }: { row: CoreRow<Opportunity> }) => {
          const instances = aidRequestInstances(row.original);
          if (instances.length >= 2 && !(row.original.location ?? "").trim()) {
            return <span>לפי מופע</span>;
          }
          return row.original.location || "";
        },
      },
      {
        field: "associationRequested",
        cell: ({ row }: { row: CoreRow<Opportunity>; }) => {
          // @ts-ignore -- change caption
          const value = row.original.associationRequested?.user;
          const heb: Partial<User> = {
            name: "שם מלא",
            phoneNumber: "מספר טלפון",
            numberID: "מספר ת.ז",
          };
          return (
            value &&
            Object.keys(value)
              .filter((key) => !!heb[key as keyof typeof heb])
              .sort((a, b) =>
                a === "phoneNumber" && b === "numberID" ? -1 : 0,
              )
              .map((key) => {
                const label = heb[key as keyof typeof heb];
                const rawValue = value[key as keyof User];
                if (key === "phoneNumber") {
                  return (
                    <Stack
                      key={label as string}
                      direction="row"
                      alignItems="center"
                      gap={0.5}
                      flexWrap="wrap"
                      sx={{ marginBlock: "0.2rem" }}
                    >
                      <Typography
                        component="span"
                        fontSize="0.9rem"
                        sx={{ whiteSpace: "pre-line" }}
                      >
                        <b>{label as string}</b>
                      </Typography>
                      <PhoneNumberCell phoneNumber={(rawValue as string) || ""} />
                    </Stack>
                  );
                }
                const fieldDisplay = rawValue as string;
                return (
                  <Typography
                    style={{ marginBlock: "0.2rem" }}
                    key={label as string}
                    title={`${label} - ${fieldDisplay}`}
                    sx={{ whiteSpace: "pre-line" }}
                    fontSize="0.9rem"
                  >
                    <b>{label as string} </b> - {fieldDisplay}
                  </Typography>
                );
              })
          );
        },
        minSize: 200,
      },
      { field: "description" },
      {
        field: "day",
        caption: "יום בשבוע",
        valueConverter: (value) => WEEK_DAYS_HEB[value.day],
      },
      { field: "comments" },
      buildCommunicationRecordsColumn(onViewCommunicationRecords),
      {
        field: "createdAt",
        cell: ({ row }: { row: CoreRow<Opportunity>; }) => {
          return row.original.createdAt
            ? new Date(row.original.createdAt).toLocaleString("he", {
              hour: "2-digit",
              minute: "2-digit",
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            })
            : " - ";
        },
        enableSorting: true,
      },
    ];
  };
